import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building,
  Users,
  Calendar,
  MoreVertical,
  FileSpreadsheet,
  UploadCloud,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  History,
  X
} from "lucide-react";
import * as XLSX from "xlsx";
import { adminService } from "../api/adminService";
import { categoryService, unitService, productService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";

export default function TenantManagement() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [importStatus, setImportStatus] = useState("idle"); // idle, reading, importing, success, error
  const [importLogs, setImportLogs] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [fileSelected, setFileSelected] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    subscription_plan: "FREE",
    address: "",
    phone: "",
    footer_note: "",
    status: "ACTIVE",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterActive]);

  const loadTenants = async () => {
    try {
      setLoading(true);

      const response = await adminService.tenants.list({ limit: 1000 });

      // Try different response structures
      let tenantsData = [];
      if (response?.data?.data) {
        tenantsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        tenantsData = response.data;
      } else if (response?.data?.tenants) {
        tenantsData = response.data.tenants;
      }

      setTenants(tenantsData);
    } catch (error) {
      console.error("Error loading tenants:", error);
      showError(
        "Gagal memuat data tenants: " +
          (error.response?.data?.message || error.message),
      );
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminService.tenants.create(formData);
      showSuccess("Tenant berhasil dibuat");
      setShowAddModal(false);
      resetForm();
      loadTenants();
    } catch (error) {
      console.error("Error creating tenant:", error);
      showError("Gagal membuat tenant");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await adminService.tenants.update(selectedTenant.id, formData);
      showSuccess("Tenant berhasil diperbarui");
      setShowEditModal(false);
      resetForm();
      loadTenants();
    } catch (error) {
      console.error("Error updating tenant:", error);
      showError("Gagal memperbarui tenant");
    }
  };

  const handleDelete = async () => {
    try {
      await adminService.tenants.delete(selectedTenant.id);
      showSuccess("Tenant berhasil dihapus");
      setShowDeleteModal(false);
      setSelectedTenant(null);
      loadTenants();
    } catch (error) {
      console.error("Error deleting tenant:", error);
      showError("Gagal menghapus tenant");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      subscription_plan: "FREE",
      address: "",
      phone: "",
      footer_note: "",
      status: "ACTIVE",
    });
    setSelectedTenant(null);
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name || "",
      slug: tenant.slug || "",
      subscription_plan: tenant.subscription_plan || "FREE",
      address: tenant.settings?.address || "",
      phone: tenant.settings?.phone || "",
      footer_note: tenant.settings?.footer_note || "",
      status: tenant.status || "ACTIVE",
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (tenant) => {
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  const openDetailModal = (tenant) => {
    setSelectedTenant(tenant);
    setFileSelected(null);
    setExcelData([]);
    setImportStatus("idle");
    setImportLogs([]);
    setImportProgress({ current: 0, total: 0 });
    setShowDetailModal(true);
  };

  const handleDownloadTemplate = (type) => {
    let headers = [];
    let sampleData = [];

    if (type === "kuliner") {
      headers = [
        "Nama Produk*",
        "Kategori*",
        "Satuan*",
        "Harga Beli",
        "Harga Jual*",
        "Stok Awal",
        "Estimasi Waktu Masak (Menit)",
        "Deskripsi",
        "Notes"
      ];
      sampleData = [
        {
          "Nama Produk*": "Nasi Goreng Spesial",
          "Kategori*": "Makanan Utama",
          "Satuan*": "Porsi",
          "Harga Beli": 12000,
          "Harga Jual*": 20000,
          "Stok Awal": 100,
          "Estimasi Waktu Masak (Menit)": 15,
          "Deskripsi": "Nasi goreng dengan telur, sosis, dan ayam",
          "Notes": "Pedas level 1-5"
        },
        {
          "Nama Produk*": "Es Teh Manis",
          "Kategori*": "Minuman",
          "Satuan*": "Gelas",
          "Harga Beli": 1000,
          "Harga Jual*": 5000,
          "Stok Awal": 200,
          "Estimasi Waktu Masak (Menit)": 3,
          "Deskripsi": "Es teh manis segar",
          "Notes": "Es dipisah"
        }
      ];
    } else {
      headers = [
        "Nama Produk*",
        "Kategori*",
        "Satuan*",
        "Harga Beli",
        "Harga Jual*",
        "Stok Awal",
        "SKU",
        "Barcode",
        "Deskripsi",
        "Min Stock",
        "Rak Lokasi",
        "Berat (gram)",
        "Dimensi (PxLxT)",
        "Warranty (Bulan)",
        "Supplier",
        "Material",
        "Notes"
      ];
      sampleData = [
        {
          "Nama Produk*": "Kaos Polos Cotton 30s",
          "Kategori*": "Fashion Pria",
          "Satuan*": "Pcs",
          "Harga Beli": 25000,
          "Harga Jual*": 50000,
          "Stok Awal": 50,
          "SKU": "SKU-KAOS-01",
          "Barcode": "8991234567890",
          "Deskripsi": "Kaos polos cotton combed 30s premium",
          "Min Stock": 10,
          "Rak Lokasi": "Rak A-1",
          "Berat (gram)": 150,
          "Dimensi (PxLxT)": "30x20x1",
          "Warranty (Bulan)": 0,
          "Supplier": "PT Sandang Jaya",
          "Material": "Cotton Combed",
          "Notes": "Ukuran L"
        }
      ];
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(sampleData, { header: headers });

      // Force Barcode and SKU columns to be formatted as text to prevent Excel scientific notation conversion (+ and E)
      if (worksheet['!ref']) {
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
          headers.forEach((h, C) => {
            if (h === "Barcode" || h === "SKU") {
              const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
              if (worksheet[cellRef]) {
                worksheet[cellRef].t = 's'; // Force string type
                worksheet[cellRef].z = '@'; // Force text format
              } else {
                worksheet[cellRef] = { t: 's', z: '@', v: '' };
              }
            }
          });
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
      XLSX.writeFile(workbook, `Template_Import_${type === "kuliner" ? "Kuliner" : "Retail"}.xlsx`);
      showSuccess("Template berhasil diunduh");
    } catch (err) {
      console.error(err);
      showError("Gagal mengunduh template");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileSelected(file);
    setImportStatus("reading");
    setImportLogs([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawJson = XLSX.utils.sheet_to_json(ws);

        if (rawJson.length === 0) {
          throw new Error("File Excel kosong atau tidak memiliki baris data.");
        }

        // Validate headers loosely
        const firstRowKeys = Object.keys(rawJson[0]);
        if (!firstRowKeys.includes("Nama Produk*") || !firstRowKeys.includes("Kategori*") || !firstRowKeys.includes("Satuan*") || !firstRowKeys.includes("Harga Jual*")) {
          throw new Error("Format kolom Excel tidak sesuai template. Pastikan kolom Nama Produk*, Kategori*, Satuan*, dan Harga Jual* tersedia.");
        }

        setExcelData(rawJson);
        setImportStatus("idle");
        showSuccess(`Berhasil membaca ${rawJson.length} baris data produk`);
      } catch (err) {
        console.error(err);
        showError(err.message);
        setImportStatus("idle");
        setFileSelected(null);
      }
    };
    reader.onerror = () => {
      showError("Gagal membaca file");
      setImportStatus("idle");
      setFileSelected(null);
    };
    reader.readAsBinaryString(file);
  };

  const handleStartImport = async () => {
    if (excelData.length === 0 || !selectedTenant) return;

    setImportStatus("importing");
    setImportLogs([]);
    setImportProgress({ current: 0, total: excelData.length });

    // Swap tenantId in localStorage temporarily
    const originalTenantId = localStorage.getItem("tenantId");
    localStorage.setItem("tenantId", selectedTenant.id);

    try {
      // 1. Fetch current categories, units, and products of this tenant
      let categories = [];
      try {
        categories = await categoryService.listAll();
      } catch (err) {
        console.error("Gagal memuat kategori awal:", err);
      }

      let units = [];
      try {
        units = await unitService.listAll();
      } catch (err) {
        console.error("Gagal memuat satuan awal:", err);
      }

      let existingProducts = [];
      try {
        existingProducts = await productService.listAll();
      } catch (err) {
        console.error("Gagal memuat produk awal:", err);
      }

      const logs = [];
      const isKuliner = "Estimasi Waktu Masak (Menit)" in excelData[0];

      // 2. Loop through each product row
      for (let i = 0; i < excelData.length; i++) {
        const row = excelData[i];
        const rowNum = i + 2; // Row index in Excel is 1-indexed plus header

        const productName = row["Nama Produk*"]?.toString()?.trim();
        const categoryName = row["Kategori*"]?.toString()?.trim();
        const unitName = row["Satuan*"]?.toString()?.trim();
        const sellPriceRaw = row["Harga Jual*"];
        const purchasePriceRaw = row["Harga Beli"];
        const initialStockRaw = row["Stok Awal"] ?? row["Stok"] ?? row["Stock"] ?? row["stok"] ?? 0;
        const initialStock = parseInt(initialStockRaw, 10) || 0;

        if (!productName || !categoryName || !unitName || sellPriceRaw === undefined) {
          logs.push({
            row: rowNum,
            status: "error",
            message: `Baris ${rowNum}: Kolom Nama Produk*, Kategori*, Satuan*, dan Harga Jual* wajib diisi.`
          });
          setImportLogs([...logs]);
          setImportProgress(prev => ({ ...prev, current: i + 1 }));
          continue;
        }

        const sku = row["SKU"]?.toString()?.trim() || null;
        const barcode = row["Barcode"]?.toString()?.trim() || null;

        // Check if product with exact same name already exists in tenant's database
        const duplicate = existingProducts.find(
          p => p.name?.toLowerCase()?.trim() === productName.toLowerCase()
        );
        if (duplicate) {
          if (initialStock > 0) {
            try {
              await productService.update(duplicate.id, {
                ...duplicate,
                stok: initialStock,
              });
              logs.push({
                row: rowNum,
                status: "success",
                message: `Baris ${rowNum}: Produk "${productName}" sudah ada di database, stok awal (${initialStock}) berhasil dimasukkan.`
              });
            } catch (updErr) {
              logs.push({
                row: rowNum,
                status: "success",
                message: `Baris ${rowNum}: Produk "${productName}" sudah ada di database. Dilewati.`
              });
            }
          } else {
            logs.push({
              row: rowNum,
              status: "success", // Mark as success since it's already there and we skip it
              message: `Baris ${rowNum}: Produk "${productName}" sudah ada di database. Dilewati (skip).`
            });
          }
          setImportLogs([...logs]);
          setImportProgress(prev => ({ ...prev, current: i + 1 }));
          continue;
        }

        // Check if barcode already exists in database
        const duplicateBarcode = barcode && existingProducts.find(
          p => p.barcode?.toString()?.trim() === barcode
        );
        if (duplicateBarcode) {
          logs.push({
            row: rowNum,
            status: "success",
            message: `Baris ${rowNum}: Barcode "${barcode}" sudah digunakan oleh produk "${duplicateBarcode.name}". Dilewati (skip).`
          });
          setImportLogs([...logs]);
          setImportProgress(prev => ({ ...prev, current: i + 1 }));
          continue;
        }

        // Check if SKU already exists in database
        const duplicateSku = sku && existingProducts.find(
          p => p.sku?.toString()?.trim() === sku
        );
        if (duplicateSku) {
          logs.push({
            row: rowNum,
            status: "success",
            message: `Baris ${rowNum}: SKU "${sku}" sudah digunakan oleh produk "${duplicateSku.name}". Dilewati (skip).`
          });
          setImportLogs([...logs]);
          setImportProgress(prev => ({ ...prev, current: i + 1 }));
          continue;
        }

        const sellPrice = parseInt(sellPriceRaw, 10) || 0;
        const purchasePrice = parseInt(purchasePriceRaw, 10) || 0;

        try {
          // Find or create Category
          let category = categories.find(
            c => c.name?.toLowerCase() === categoryName.toLowerCase()
          );
          if (!category) {
            try {
              const createRes = await categoryService.create({ name: categoryName, status: "ACTIVE" });
              category = createRes?.data || createRes;
            } catch (createErr) {
              console.warn("Category creation error / may exist:", createErr);
            }
            if (!category || !category.id) {
              const freshCats = await categoryService.listAll();
              category = freshCats.find(c => c.name?.toLowerCase() === categoryName.toLowerCase());
            }
            if (category && category.id) {
              categories.push(category);
            } else {
              throw new Error(`Kategori "${categoryName}" gagal dibuat.`);
            }
          }

          // Find or create Unit
          let unit = units.find(
            u => u.name?.toLowerCase() === unitName.toLowerCase()
          );
          if (!unit) {
            try {
              const createUnitRes = await unitService.create({ name: unitName, status: "ACTIVE" });
              unit = createUnitRes?.data || createUnitRes;
            } catch (unitErr) {
              console.warn("Unit creation error / may exist:", unitErr);
            }
            if (!unit || !unit.id) {
              const freshUnits = await unitService.listAll();
              unit = freshUnits.find(u => u.name?.toLowerCase() === unitName.toLowerCase());
            }
            if (unit && unit.id) {
              units.push(unit);
            } else {
              throw new Error(`Satuan "${unitName}" gagal dibuat.`);
            }
          }

          // Build product payload
          const description = row["Deskripsi"]?.toString()?.trim() || null;
          const notes = row["Notes"]?.toString()?.trim() || null;
          
          const minStock = parseInt(row["Min Stock"], 10) || 0;
          const rackLocation = row["Rak Lokasi"]?.toString()?.trim() || null;
          const weight = parseFloat(row["Berat (gram)"]) || null;
          const dimensions = row["Dimensi (PxLxT)"]?.toString()?.trim() || null;
          const warrantyMonths = parseInt(row["Warranty (Bulan)"], 10) || null;
          const supplier = row["Supplier"]?.toString()?.trim() || null;
          const material = row["Material"]?.toString()?.trim() || null;

          const prepTimeMins = parseInt(row["Estimasi Waktu Masak (Menit)"], 10) || null;

          const payload = {
            name: productName,
            sku: sku,
            barcode: barcode,
            description: description,
            min_stock: minStock,
            rack_location: rackLocation,
            weight: weight,
            dimensions: dimensions,
            material: material,
            warranty_months: warrantyMonths,
            supplier: supplier,
            notes: notes,
            category_id: category.id,
            unit_id: unit.id,
            purchase_price: purchasePrice,
            sell_price: sellPrice,
            stok: initialStock,
            variants: []
          };

          // If Culinary and prepTimeMins is specified, create a default variant to store it
          if (isKuliner && prepTimeMins !== null) {
            payload.variants = [{
              prep_time_mins: prepTimeMins,
              purchase_price: purchasePrice,
              sell_price: sellPrice,
              stok: initialStock
            }];
          }

          await productService.create(payload);

          logs.push({
            row: rowNum,
            status: "success",
            message: `Baris ${rowNum}: Produk "${productName}" berhasil diimport dengan stok awal ${initialStock}.`
          });
        } catch (err) {
          console.error(`Error importing row ${rowNum}:`, err);
          logs.push({
            row: rowNum,
            status: "error",
            message: `Baris ${rowNum}: Gagal membuat produk "${productName}". ${err.message || ""}`
          });
        }

        setImportLogs([...logs]);
        setImportProgress(prev => ({ ...prev, current: i + 1 }));
      }

      const hasError = logs.some(log => log.status === "error");
      if (hasError) {
        setImportStatus("error");
        showError("Proses import selesai dengan beberapa kesalahan.");
      } else {
        setImportStatus("success");
        showSuccess("Semua produk berhasil diimport!");
      }
    } catch (error) {
      console.error("General import error:", error);
      showError("Terjadi kesalahan sistem saat proses import.");
      setImportStatus("error");
    } finally {
      // Restore original tenantId
      if (originalTenantId) {
        localStorage.setItem("tenantId", originalTenantId);
      } else {
        localStorage.removeItem("tenantId");
      }
    }
  };

  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch =
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.domain?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterActive === "active")
      return matchesSearch && tenant.status === "ACTIVE";
    if (filterActive === "inactive")
      return matchesSearch && tenant.status !== "ACTIVE";
    return matchesSearch;
  });

  const totalEntries = filteredTenants.length;
  const maxPages = Math.ceil(totalEntries / itemsPerPage);
  const startEntry = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);
  const paginatedTenants = filteredTenants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Tenant Management
          </h1>
          <p className="text-gray-600">
            Manage tenant organizations and their configurations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Tenant
          </button>
          <button
            onClick={loadTenants}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            disabled={loading}>
            {loading ? "Loading..." : "Reload Tenants"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterActive("all")}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterActive === "all"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              All ({tenants.length})
            </button>
            <button
              onClick={() => setFilterActive("active")}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterActive === "active"
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Active ({tenants.filter((t) => t.status === "ACTIVE").length})
            </button>
            <button
              onClick={() => setFilterActive("inactive")}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterActive === "inactive"
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Inactive ({tenants.filter((t) => t.status !== "ACTIVE").length})
            </button>
          </div>
        </div>
      </div>

      {/* Tenant Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedTenants.map((tenant) => (
            <div
              key={tenant.id}
              onClick={() => openDetailModal(tenant)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {tenant.name}
                      </h3>
                      <p className="text-sm text-gray-500">{tenant.slug}</p>
                    </div>
                  </div>
                  <div className="relative">
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>Plan: {tenant.subscription_plan || "Unknown"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Created:{" "}
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {tenant.settings?.address && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Address:</span>{" "}
                      {tenant.settings.address}
                    </p>
                  )}
                  {tenant.settings?.phone && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Phone:</span>{" "}
                      {tenant.settings.phone}
                    </p>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      tenant.status === "ACTIVE"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}>
                    {tenant.status || "Unknown"}
                  </span>
                  <div className="flex gap-1.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/tenants/${tenant.id}/audit`); }}
                      className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors shadow-sm"
                      title="Audit Trail">
                      <History className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(tenant); }}
                      className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                      title="Edit">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(tenant); }}
                      className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                      title="Hapus">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredTenants.length > 0 && maxPages > 1 && (
            <div className="col-span-full mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-6 py-4 rounded-xl border border-gray-200">
                <div className="text-sm text-gray-500">
                  Menampilkan <span className="font-semibold text-gray-700">{startEntry}</span> -{" "}
                  <span className="font-semibold text-gray-700">{endEntry}</span> dari{" "}
                  <span className="font-semibold text-gray-700">{totalEntries}</span> tenant
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Sebelumnya
                  </button>
                  
                  {Array.from({ length: maxPages }).map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 text-xs font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "border border-gray-200 hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPages))}
                    disabled={currentPage === maxPages}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Selanjutnya
                  </button>
                </div>
              </div>
            </div>
          )}

          {filteredTenants.length === 0 && !loading && (
            <div className="col-span-full">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No tenants found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || filterActive !== "all"
                    ? "Try adjusting your search or filters"
                    : "Get started by creating your first tenant"}
                </p>
                {!searchTerm && filterActive === "all" && (
                  <button
                    onClick={() => { resetForm(); setShowAddModal(true); }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                    Create Tenant
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Tenant & Premium Import Modal */}
      {showDetailModal && selectedTenant && (
        <div
          onClick={() => { if (importStatus !== "importing") setShowDetailModal(false); }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 modal-backdrop-enter">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden modal-panel-enter shadow-2xl border border-gray-100">
            
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  Detail Tenant
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Kelola informasi dan import data produk tenant</p>
              </div>
              <button
                disabled={importStatus === "importing"}
                onClick={() => setShowDetailModal(false)}
                className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              
              {/* Tenant Info Card */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Nama Tenant</span>
                  <p className="text-sm font-semibold text-slate-800">{selectedTenant.name}</p>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Slug</span>
                  <p className="text-sm font-mono text-slate-700">{selectedTenant.slug}</p>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Paket Layanan</span>
                  <div>
                    <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-600 text-white mt-0.5 shadow-sm">
                      {selectedTenant.subscription_plan || "FREE"}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Status Akun</span>
                  <div>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mt-0.5 shadow-sm ${
                      selectedTenant.status === "ACTIVE"
                        ? "bg-green-500 text-white"
                        : "bg-red-500 text-white"
                    }`}>
                      {selectedTenant.status || "Unknown"}
                    </span>
                  </div>
                </div>
                {selectedTenant.settings?.address && (
                  <div className="col-span-2">
                    <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Alamat Lengkap</span>
                    <p className="text-sm text-slate-700">{selectedTenant.settings.address}</p>
                  </div>
                )}
              </div>

              {/* Excel Import Premium Section */}
              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-base font-bold text-slate-800">Premium: Import Master Data Produk</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Unggah file Excel yang berisi daftar produk untuk dimasukkan secara otomatis ke dalam database tenant ini.
                </p>

                {/* Download Templates */}
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <button
                    type="button"
                    onClick={() => handleDownloadTemplate("retail")}
                    className="flex items-center justify-center gap-2 p-3 bg-white border border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 rounded-xl text-sm font-medium transition-all shadow-sm group"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600 group-hover:scale-110 transition-transform" />
                    Template Retail (Web)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownloadTemplate("kuliner")}
                    className="flex items-center justify-center gap-2 p-3 bg-white border border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 rounded-xl text-sm font-medium transition-all shadow-sm group"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600 group-hover:scale-110 transition-transform" />
                    Template Kuliner (Apps)
                  </button>
                </div>

                {/* Upload File Box */}
                {importStatus !== "importing" && (
                  <div className="border-2 border-dashed border-gray-200 hover:border-blue-400 rounded-xl p-6 text-center cursor-pointer relative bg-gray-50 hover:bg-white group transition-colors">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                    <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 mx-auto mb-2 transition-colors" />
                    {fileSelected ? (
                      <div>
                        <p className="text-sm font-semibold text-blue-600">{fileSelected.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{(fileSelected.size / 1024).toFixed(1)} KB - Klik atau seret untuk mengganti</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Pilih file Excel template hasil pengisian</p>
                        <p className="text-xs text-gray-500 mt-1">Format yang didukung: .xlsx atau .xls</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action & Progress Bar */}
                {importStatus === "importing" && (
                  <div className="space-y-3 bg-slate-50 border rounded-xl p-4">
                    <div className="flex justify-between items-center text-sm font-semibold text-slate-700">
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        Mengimpor Data Produk...
                      </span>
                      <span>
                        {importProgress.current} / {importProgress.total} Produk ({Math.round((importProgress.current / importProgress.total) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 h-2.5 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-600 h-full transition-all duration-300 rounded-full"
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Import Status Indicator */}
                {importStatus === "success" && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-start gap-3 mt-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">Proses Import Selesai!</p>
                      <p className="text-xs text-emerald-700 mt-1">
                        Daftar produk baru telah berhasil di-parse dan tersimpan di database tenant. Periksa panel log di bawah untuk detail baris.
                      </p>
                    </div>
                  </div>
                )}

                {/* Panel Log Real-time */}
                {importLogs.length > 0 && (
                  <div className="mt-5 space-y-2">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Log Detail Transaksi Import</span>
                    <div className="max-h-48 overflow-y-auto bg-slate-900 text-slate-200 border rounded-xl p-4 font-mono text-xs space-y-1.5 scrollbar-thin">
                      {importLogs.map((log, idx) => (
                        <div
                          key={idx}
                          className={`flex items-start gap-2 ${
                            log.status === "success" ? "text-emerald-400" : "text-rose-400"
                          }`}
                        >
                          <span className="flex-shrink-0 font-bold">[{log.status === "success" ? "SUCCESS" : "ERROR"}]</span>
                          <span>{log.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 flex-shrink-0">
              <button
                type="button"
                disabled={importStatus === "importing"}
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Tutup Detail
              </button>
              {fileSelected && excelData.length > 0 && importStatus !== "importing" && importStatus !== "success" && (
                <button
                  type="button"
                  onClick={handleStartImport}
                  className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-md shadow-blue-200"
                >
                  Mulai Import ({excelData.length} Produk)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Modal */}
      {showAddModal && (
        <div
          onClick={() => { setShowAddModal(false); resetForm(); }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 modal-backdrop-enter">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden modal-panel-enter">
            <form onSubmit={handleCreate} className="flex flex-col max-h-[85vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900">
                  Add New Tenant
                </h2>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter tenant name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug / Subdomain *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="tenant-slug"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan
                  </label>
                  <select
                    value={formData.subscription_plan}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subscription_plan: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="FREE">FREE</option>
                    <option value="BASIC">BASIC</option>
                    <option value="PREMIUM">PREMIUM</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        phone: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+62 xxx xxx xxxx"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        address: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter tenant address"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Footer Note
                  </label>
                  <textarea
                    value={formData.footer_note}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        footer_note: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter receipt footer note"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Create Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && (
        <div
          onClick={() => { setShowEditModal(false); resetForm(); }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 modal-backdrop-enter">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden modal-panel-enter">
            <form onSubmit={handleUpdate} className="flex flex-col max-h-[85vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit Tenant
                </h2>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug / Subdomain *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan
                  </label>
                  <select
                    value={formData.subscription_plan}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subscription_plan: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="FREE">FREE</option>
                    <option value="BASIC">BASIC</option>
                    <option value="PREMIUM">PREMIUM</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        phone: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        address: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Footer Note
                  </label>
                  <textarea
                    value={formData.footer_note}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        footer_note: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Update Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          onClick={() => setShowDeleteModal(false)}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 modal-backdrop-enter">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg max-w-md w-full modal-panel-enter">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete Tenant
                  </h3>
                  <p className="text-gray-600">This action cannot be undone.</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete{" "}
                  <strong>{selectedTenant?.name}</strong>? This will permanently
                  remove the tenant and all associated data.
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedTenant(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  Delete Tenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
